<template>
  <div class="ctc-step">
    <div class="ctc-step__eyebrow">PHASE 2 · ÉTAPE 4 SUR 4</div>
    <h2 class="ctc-step__title">Vérifier et lancer</h2>

    <section
      class="ctc-review"
      :class="`ctc-review--${status.tone}`"
      aria-labelledby="ctc-review-status"
    >
      <!-- Can I launch? The three checks are the steps that feed the launch. -->
      <header class="ctc-review__status">
        <span class="ctc-review__status-icon" aria-hidden="true">
          <v-icon :color="status.color">{{ status.icon }}</v-icon>
        </span>
        <div class="ctc-review__status-copy">
          <strong id="ctc-review-status">{{ status.title }}</strong>
          <small>{{ status.text }}</small>
        </div>
        <ul class="ctc-review__checks" aria-label="Contrôles avant lancement">
          <li
            v-for="check in checks"
            :key="check.step"
            :class="`ctc-review__check--${check.state}`"
          >
            <v-icon x-small color="currentColor">{{ check.icon }}</v-icon>
            {{ check.label }}
          </li>
        </ul>
        <rmr-action-button
          v-if="blocker"
          :label="`Corriger à l’étape ${blocker.step}`"
          :icon="icons.mdiPencil"
          size="sm"
          class="ctc-review__fix"
          @click="$emit('edit-step', blocker.step)"
        />
      </header>

      <!-- What the button does, in order: AIF, WordPress, then the editor. -->
      <ol class="ctc-review__flow" aria-label="Après le lancement">
        <li v-for="(stage, index) in flow" :key="stage.title">
          <span
            class="ctc-review__flow-icon"
            :class="`ctc-review__flow-icon--${stage.tone}`"
            aria-hidden="true"
          >
            <v-icon small color="currentColor">{{ stage.icon }}</v-icon>
          </span>
          <span class="ctc-review__flow-copy">
            <strong>{{ stage.title }}</strong>
            <small>{{ stage.text }}</small>
          </span>
          <v-icon
            v-if="index < flow.length - 1"
            class="ctc-review__flow-arrow"
            :color="colors.subtle"
            aria-hidden="true"
          >
            {{ icons.mdiArrowRight }}
          </v-icon>
        </li>
      </ol>

      <!-- The product the article is about. -->
      <div class="ctc-review__hero">
        <span class="ctc-review__media">
          <img
            v-if="productImage"
            :src="productImage"
            alt=""
            width="72"
            height="72"
          />
          <ctc-product-visual
            v-else
            :icon="product.visual.icon"
            :color="product.visual.color"
          />
        </span>
        <div class="ctc-review__hero-copy">
          <small class="ctc-review__kicker">Produit de l’article</small>
          <h3 class="ctc-review__product-title" :title="product.title">
            {{ product.title }}
          </h3>
          <span class="ctc-review__tags">
            <span class="ctc-review__tag ctc-review__tag--site">
              {{ siteName }}
            </span>
            <span class="ctc-review__tag">{{ editorialFormatLabel }}</span>
            <span v-if="signature" class="ctc-review__tag">
              {{ signature }}
            </span>
          </span>
        </div>
        <rmr-action-button
          label="Changer de produit ou de site"
          :icon="icons.mdiPencil"
          variant="ghost"
          size="sm"
          class="ctc-review__change"
          @click="$emit('edit-step', 1)"
        />
      </div>

      <div class="ctc-review__sections">
        <section
          class="ctc-review__section ctc-review__section--article"
          aria-labelledby="ctc-review-article"
        >
          <header class="ctc-review__section-header">
            <span
              class="ctc-review__section-icon ctc-review__section-icon--article"
            >
              <v-icon small color="currentColor">
                {{ icons.mdiFileDocumentOutline }}
              </v-icon>
            </span>
            <h3 id="ctc-review-article">Article</h3>
            <rmr-action-button
              label="Modifier"
              :icon="icons.mdiPencil"
              variant="ghost"
              size="xs"
              aria-label="Modifier le format et la signature (étape 3)"
              @click="$emit('edit-step', 3)"
            />
          </header>
          <dl class="ctc-review__rows">
            <dt>Format</dt>
            <dd>{{ editorialFormatLabel }}</dd>
            <dt>Signature</dt>
            <dd :title="signature">{{ signature || "Aucune signature" }}</dd>
          </dl>
        </section>

        <section
          class="ctc-review__section ctc-review__section--publication"
          aria-labelledby="ctc-review-publication"
        >
          <header class="ctc-review__section-header">
            <span class="ctc-review__section-icon ctc-review__section-icon--wp">
              <v-icon small color="currentColor">{{
                icons.mdiWordpress
              }}</v-icon>
            </span>
            <h3 id="ctc-review-publication">WordPress</h3>
            <rmr-action-button
              label="Modifier"
              :icon="icons.mdiPencil"
              variant="ghost"
              size="xs"
              aria-label="Modifier la publication WordPress (étape 3)"
              @click="$emit('edit-step', 3)"
            />
          </header>
          <dl class="ctc-review__rows">
            <dt>Statut</dt>
            <dd>
              <span
                class="ctc-review__dot"
                :class="`ctc-review__dot--${postStatus}`"
                aria-hidden="true"
              />
              {{ publication.label }}
            </dd>
            <template v-if="publication.date">
              <dt>Date</dt>
              <dd>{{ publication.date }}</dd>
            </template>
            <dt>Site</dt>
            <dd>{{ siteName }}</dd>
          </dl>
          <p
            class="ctc-review__hint"
            :class="{ 'ctc-review__hint--warning': publication.warning }"
          >
            <v-icon x-small color="currentColor">
              {{
                publication.warning
                  ? icons.mdiAlertOutline
                  : icons.mdiInformationOutline
              }}
            </v-icon>
            <span>{{ publication.warning || publication.note }}</span>
          </p>
        </section>

        <section
          class="ctc-review__section ctc-review__section--affilizz"
          aria-labelledby="ctc-review-affilizz"
        >
          <header class="ctc-review__section-header">
            <span
              class="ctc-review__section-icon ctc-review__section-icon--affilizz"
            >
              <v-icon small color="currentColor">{{
                icons.mdiLinkVariant
              }}</v-icon>
            </span>
            <h3 id="ctc-review-affilizz">Contenus Affilizz</h3>
            <rmr-action-button
              label="Modifier"
              :icon="icons.mdiPencil"
              variant="ghost"
              size="xs"
              aria-label="Modifier le widget et l’affiliation Affilizz (étape 2)"
              @click="$emit('edit-step', 2)"
            />
          </header>

          <div class="ctc-review__content">
            <span class="ctc-review__content-copy">
              <strong>CTA du produit principal</strong>
              <small>Bouton d’offre dans l’introduction</small>
            </span>
            <span
              class="ctc-review__state"
              :class="`ctc-review__state--${ctaState}`"
            >
              <v-icon x-small color="currentColor">
                {{ stateIcon(ctaState) }}
              </v-icon>
              {{ stateLabel(ctaState) }}
            </span>
          </div>

          <div class="ctc-review__content ctc-review__content--widget">
            <div class="ctc-review__content-head">
              <span class="ctc-review__content-copy">
                <strong>
                  {{
                    widgetEnabled
                      ? `Widget ${structure.type} · ${structure.name}`
                      : "Widget comparatif"
                  }}
                </strong>
                <small>
                  {{
                    widgetEnabled
                      ? structure.summary
                      : "Aucun widget ne sera intégré dans l’article."
                  }}
                </small>
              </span>
              <span
                class="ctc-review__state"
                :class="`ctc-review__state--${widgetState}`"
              >
                <v-icon x-small color="currentColor">
                  {{ stateIcon(widgetState) }}
                </v-icon>
                {{ stateLabel(widgetState) }}
              </span>
            </div>

            <!-- BOX: one chip per tab; CARD: the products of the card. -->
            <ol
              v-if="widgetEnabled && structure.type === 'BOX'"
              class="ctc-review__chips"
              aria-label="Onglets du comparateur"
            >
              <li
                v-for="(tab, index) in structure.tabs"
                :key="tab.label"
                class="ctc-review__chip"
              >
                <span class="ctc-review__chip-num">{{ index + 1 }}</span>
                <span class="ctc-review__chip-thumb">
                  <img
                    v-if="tab.products[0] && tab.products[0].imageUrl"
                    :src="tab.products[0].imageUrl"
                    alt=""
                    width="26"
                    height="26"
                    loading="lazy"
                  />
                  <v-icon v-else x-small :color="colors.subtle">
                    {{ icons.mdiImageOffOutline }}
                  </v-icon>
                </span>
                <span class="ctc-review__chip-title" :title="tab.title">
                  {{ tab.title }}
                </span>
                <small>{{ tab.products.length }} coloris</small>
              </li>
            </ol>
            <ul
              v-else-if="widgetEnabled"
              class="ctc-review__chips"
              aria-label="Produits de la carte"
            >
              <li
                v-for="item in structure.products"
                :key="item.productLocalizedId"
                class="ctc-review__chip"
              >
                <span class="ctc-review__chip-thumb">
                  <img
                    v-if="item.imageUrl"
                    :src="item.imageUrl"
                    alt=""
                    width="26"
                    height="26"
                    loading="lazy"
                  />
                  <v-icon v-else x-small :color="colors.subtle">
                    {{ icons.mdiImageOffOutline }}
                  </v-icon>
                </span>
                <span class="ctc-review__chip-title" :title="item.title">
                  {{ item.title }}
                </span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      <footer class="ctc-review__foot">
        <v-icon x-small :color="colors.muted">
          {{ icons.mdiInformationOutline }}
        </v-icon>
        Les contenus Affilizz vérifiés à l’étape 2 sont réutilisés tels quels :
        rien n’est recréé chez Affilizz.
      </footer>
    </section>
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiArrowRight,
  mdiCheck,
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiClose,
  mdiFileDocumentOutline,
  mdiImageOffOutline,
  mdiInformationOutline,
  mdiLinkVariant,
  mdiMinus,
  mdiPencil,
  mdiRobotOutline,
  mdiWordpress,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import {
  RMR_DANGER,
  RMR_GREEN_DARK,
  RMR_TEXT_MUTED,
  RMR_TEXT_SUBTLE,
} from "@/styles/tokens";

import { COMPONENT_STATE_LABELS, EDITORIAL_FORMAT_LABELS } from "../constants";
import {
  componentStateOf,
  formatDateTime,
  widgetStructureOf,
} from "../ctc-model";
import CtcProductVisual from "../shared/CtcProductVisual.vue";

// What WordPress does with the generated article, per requested status.
const PUBLICATIONS = {
  draft: {
    label: "Brouillon",
    note: "Rien n’est publié automatiquement : l’article arrive en brouillon.",
    warning: "",
  },
  publish: {
    label: "Publication immédiate",
    note: "",
    warning:
      "Sans relecture : l’article sera mis en ligne dès que WordPress le recevra.",
  },
  future: {
    label: "Programmée",
    note: "",
    warning: "Sans relecture : l’article sera mis en ligne à la date prévue.",
  },
};

const STATE_ICONS = {
  affiliated: mdiCheck,
  not_affiliated: mdiClose,
  pending: mdiClockOutline,
  disabled: mdiMinus,
};

const CHECKS = [
  { step: 1, label: "Produit" },
  { step: 2, label: "Affilizz" },
  { step: 3, label: "Paramètres" },
];

export default {
  name: "CtcLaunchStep",
  components: { CtcProductVisual, RmrActionButton },
  props: {
    product: {
      type: Object,
      required: true,
    },
    site: {
      type: Object,
      default: null,
    },
    widgetEnabled: {
      type: Boolean,
      default: true,
    },
    widgetType: {
      type: String,
      default: "BOX",
    },
    similarProducts: {
      type: Array,
      default: () => [],
    },
    signature: {
      type: String,
      required: true,
    },
    editorialFormat: {
      type: String,
      default: "focus_product",
    },
    postStatus: {
      type: String,
      default: "draft",
    },
    scheduledAt: {
      type: Date,
      default: null,
    },
    // Affiliation verified in phase 1 (CTA and widget separately).
    affiliation: {
      type: Object,
      default: null,
    },
    // useCtcCreateFlow().launchBlocker: {step, message} or null.
    blocker: {
      type: Object,
      default: null,
    },
    // The last launch was refused by the server: its reason is in the footer.
    rejected: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const siteName = computed(() =>
      props.site ? props.site.name : "Site à sélectionner"
    );

    const status = computed(() => {
      if (props.blocker) {
        return {
          tone: "blocked",
          icon: mdiAlertCircleOutline,
          color: RMR_DANGER,
          title: "Lancement impossible",
          text: props.blocker.message,
        };
      }
      if (props.rejected) {
        return {
          tone: "blocked",
          icon: mdiAlertCircleOutline,
          color: RMR_DANGER,
          title: "Lancement refusé",
          text: "Le motif est indiqué au-dessus des boutons : corrigez-le, puis relancez.",
        };
      }
      return {
        tone: "ready",
        icon: mdiCheckCircleOutline,
        color: RMR_GREEN_DARK,
        title: "Prêt à lancer la génération",
        text: "Vérifiez une dernière fois le récapitulatif ci-dessous.",
      };
    });

    // Steps before the blocking one are done; the blocking one is not; the
    // following ones are not judged yet.
    const checks = computed(() =>
      CHECKS.map((check) => {
        let state = "ok";
        if (props.blocker && check.step === props.blocker.step) {
          state = "blocked";
        } else if (props.blocker && check.step > props.blocker.step) {
          state = "unknown";
        }
        return {
          ...check,
          state,
          icon: { ok: mdiCheck, blocked: mdiClose, unknown: mdiMinus }[state],
        };
      })
    );

    const publication = computed(() => {
      const entry = PUBLICATIONS[props.postStatus] || PUBLICATIONS.draft;
      const date =
        props.postStatus === "future" && props.scheduledAt
          ? formatDateTime(props.scheduledAt.toISOString())
          : "";
      return { ...entry, date };
    });

    // After the launch: AIF writes, WordPress receives, then the editor.
    const flow = computed(() => {
      const site = siteName.value;
      const writing = {
        title: "Rédaction par l’IA",
        text: "Génération AIF avec les contenus Affilizz vérifiés",
        icon: mdiRobotOutline,
        tone: "ai",
      };
      if (props.postStatus === "publish") {
        return [
          writing,
          {
            title: "Publication WordPress",
            text: `Mis en ligne sur ${site} dès réception`,
            icon: mdiWordpress,
            tone: "wp",
          },
          {
            title: "Sans relecture",
            text: "Relisez l’article en ligne",
            icon: mdiAlertOutline,
            tone: "warning",
          },
        ];
      }
      if (props.postStatus === "future") {
        return [
          writing,
          {
            title: "Programmé sur WordPress",
            text: `Sur ${site}, ${
              publication.value.date || "à la date prévue"
            }`,
            icon: mdiWordpress,
            tone: "wp",
          },
          {
            title: "Sans relecture",
            text: "En ligne à la date prévue",
            icon: mdiAlertOutline,
            tone: "warning",
          },
        ];
      }
      return [
        writing,
        {
          title: "Brouillon WordPress",
          text: `Envoyé sur ${site}, non publié`,
          icon: mdiWordpress,
          tone: "wp",
        },
        {
          title: "Votre relecture",
          text: "Vous publiez quand c’est prêt",
          icon: mdiPencil,
          tone: "editor",
        },
      ];
    });

    return {
      checks,
      colors: { muted: RMR_TEXT_MUTED, subtle: RMR_TEXT_SUBTLE },
      ctaState: computed(() =>
        componentStateOf(props.affiliation && props.affiliation.link)
      ),
      editorialFormatLabel: computed(
        () => EDITORIAL_FORMAT_LABELS[props.editorialFormat] || "Focus produit"
      ),
      flow,
      icons: {
        mdiAlertOutline,
        mdiArrowRight,
        mdiFileDocumentOutline,
        mdiImageOffOutline,
        mdiInformationOutline,
        mdiLinkVariant,
        mdiPencil,
        mdiWordpress,
      },
      productImage: computed(() => props.product.imageUrl || ""),
      publication,
      siteName,
      stateIcon: (state) => STATE_ICONS[state],
      stateLabel: (state) => COMPONENT_STATE_LABELS[state],
      status,
      structure: computed(() =>
        widgetStructureOf(props.widgetType, props.similarProducts)
      ),
      widgetState: computed(() =>
        componentStateOf(
          props.affiliation && props.affiliation.widget,
          props.widgetEnabled
        )
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
@import "./_ctc-step.scss";

.ctc-review {
  overflow: hidden;
  margin-top: $rmr-space-lg;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-2xl;
  background: $rmr-surface-0;
}

// Status band: green when ready, red when something blocks.
.ctc-review__status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 14px;
  padding: 18px 22px;
  border-bottom: 1px solid $rmr-border;
}

.ctc-review--ready .ctc-review__status {
  background: linear-gradient(90deg, $rmr-green-bg, $rmr-surface-0 70%);
}

.ctc-review--blocked .ctc-review__status {
  background: linear-gradient(90deg, $rmr-red-bg, $rmr-surface-0 70%);
}

.ctc-review__status-icon {
  display: grid;
  width: 40px;
  height: 40px;
  flex: none;
  place-items: center;
  border: 1px solid $rmr-border;
  border-radius: $rmr-radius-lg;
  background: $rmr-surface-0;
}

.ctc-review__status-copy {
  min-width: 0;
  flex: 1 1 240px;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-stat;
  }

  small {
    margin-top: 1px;
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
  }
}

.ctc-review__checks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border: 1px solid $rmr-border-hover;
    border-radius: $rmr-radius-pill;
    background: $rmr-surface-0;
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
    font-weight: 600;
  }
}

.ctc-review__check--ok {
  border-color: rgba($rmr-green, 0.3) !important;
  color: $rmr-green-dark !important;
}

.ctc-review__check--blocked {
  border-color: rgba($rmr-danger, 0.3) !important;
  color: $rmr-danger !important;
}

.ctc-review ::v-deep .v-btn {
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.ctc-review ::v-deep .v-btn:focus-visible {
  outline: 2px solid $rmr-indigo;
  outline-offset: 2px;
}

// After the launch, left to right.
.ctc-review__flow {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 14px 22px;
  border-bottom: 1px solid $rmr-border;
  background: $rmr-surface-2;
  list-style: none;

  li {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 10px;
  }
}

.ctc-review__flow-icon {
  display: grid;
  width: 28px;
  height: 28px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-sm;
}

.ctc-review__flow-icon--ai {
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
}

.ctc-review__flow-icon--wp {
  background: $rmr-sessions-soft;
  color: $rmr-sessions;
}

.ctc-review__flow-icon--editor,
.ctc-review__flow-icon--warning {
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
}

.ctc-review__flow-copy {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-body;
  }

  small {
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
  }
}

.ctc-review__flow-arrow {
  flex: none;
}

// The product.
.ctc-review__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid $rmr-border;
}

.ctc-review__media {
  display: grid;
  width: 72px;
  height: 72px;
  flex: none;
  place-items: center;
  overflow: hidden;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-2;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ctc-review__hero-copy {
  min-width: 0;
  flex: 1;
}

.ctc-review__kicker {
  color: $rmr-text-subtle;
  font-size: $rmr-fs-sm;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.ctc-review__product-title {
  display: -webkit-box;
  margin: 2px 0 6px;
  overflow: hidden;
  color: $rmr-text-primary;
  font-size: $rmr-fs-stat;
  font-weight: 700;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ctc-review__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ctc-review__tag {
  max-width: 100%;
  overflow: hidden;
  padding: 3px 9px;
  border-radius: $rmr-radius-sm;
  background: $rmr-surface-2;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-md;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-review__tag--site {
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
}

.ctc-review__change {
  flex: none;
  border: 1px solid $rmr-border-hover !important;
}

// Three summaries side by side, then stacked.
.ctc-review__sections {
  display: grid;
  grid-template-areas: "article publication affilizz";
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.25fr);
}

.ctc-review__section {
  min-width: 0;
  padding: 18px 22px;
}

.ctc-review__section--article {
  grid-area: article;
}

.ctc-review__section--publication {
  grid-area: publication;
  border-left: 1px solid $rmr-border;
}

.ctc-review__section--affilizz {
  grid-area: affilizz;
  border-left: 1px solid $rmr-border;
}

.ctc-review__section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  h3 {
    min-width: 0;
    flex: 1;
    margin: 0;
    color: $rmr-text-primary;
    font-size: $rmr-fs-title;
    font-weight: 700;
  }
}

.ctc-review__section-icon {
  display: grid;
  width: 28px;
  height: 28px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-sm;
}

.ctc-review__section-icon--article {
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
}

.ctc-review__section-icon--wp {
  background: $rmr-sessions-soft;
  color: $rmr-sessions;
}

.ctc-review__section-icon--affilizz {
  background: $rmr-green-bg;
  color: $rmr-green-dark;
}

.ctc-review__rows {
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr);
  row-gap: 10px;
  margin: 0;
  font-size: $rmr-fs-body;

  dt {
    color: $rmr-text-muted;
  }

  dd {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: $rmr-text-primary;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ctc-review__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
  vertical-align: middle;
}

.ctc-review__dot--draft {
  background: $rmr-orange;
}

.ctc-review__dot--publish {
  background: $rmr-green;
}

.ctc-review__dot--future {
  background: $rmr-indigo;
}

.ctc-review__hint {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 12px 0 0;
  padding: 8px 10px;
  border-radius: $rmr-radius-sm;
  background: $rmr-surface-2;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;

  .v-icon {
    margin-top: 2px;
  }
}

.ctc-review__hint--warning {
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
  font-weight: 600;
}

// Affilizz contents: one bordered row each.
.ctc-review__content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border: 1px solid $rmr-border;
  border-radius: $rmr-radius-md;
}

.ctc-review__content + .ctc-review__content {
  margin-top: 8px;
}

.ctc-review__content--widget {
  flex-direction: column;
  align-items: stretch;
}

.ctc-review__content-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ctc-review__content-copy {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
  }

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-body;
  }

  small {
    color: $rmr-text-muted;
    font-size: $rmr-fs-md;
  }
}

.ctc-review__state {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: $rmr-radius-pill;
  background: $rmr-surface-2;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-base;
  font-weight: 700;
}

.ctc-review__state--affiliated {
  background: $rmr-green-bg;
  color: $rmr-green-dark;
}

.ctc-review__state--not_affiliated {
  background: $rmr-red-bg;
  color: $rmr-danger;
}

.ctc-review__state--pending {
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
}

.ctc-review__chips {
  display: grid;
  gap: 6px;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
}

.ctc-review__chip {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: $rmr-radius-sm;
  background: $rmr-surface-2;

  small {
    flex: none;
    color: $rmr-text-muted;
    font-size: $rmr-fs-base;
  }
}

.ctc-review__chip-num {
  display: grid;
  width: 20px;
  height: 20px;
  flex: none;
  place-items: center;
  border: 1px solid rgba($rmr-indigo, 0.2);
  border-radius: $rmr-radius-xs;
  background: $rmr-surface-0;
  color: $rmr-indigo;
  font-size: $rmr-fs-base;
  font-weight: 700;
}

.ctc-review__chip-thumb {
  display: grid;
  width: 26px;
  height: 26px;
  flex: none;
  place-items: center;
  overflow: hidden;
  border-radius: $rmr-radius-xs;
  background: $rmr-surface-0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ctc-review__chip-title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: $rmr-text-primary;
  font-size: $rmr-fs-md;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-review__foot {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 22px;
  border-top: 1px solid $rmr-border;
  background: $rmr-surface-2;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;
}

@media (max-width: 1100px) {
  .ctc-review__sections {
    grid-template-areas:
      "article publication"
      "affilizz affilizz";
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .ctc-review__section--affilizz {
    border-top: 1px solid $rmr-border;
    border-left: 0;
  }
}

@media (max-width: 760px) {
  .ctc-review__flow {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-review__flow-arrow {
    display: none !important;
  }

  .ctc-review__hero {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .ctc-review__hero-copy {
    flex-basis: calc(100% - 88px);
  }

  .ctc-review__change {
    margin-left: 88px;
  }

  .ctc-review__sections {
    grid-template-areas:
      "article"
      "publication"
      "affilizz";
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-review__section--publication {
    border-top: 1px solid $rmr-border;
    border-left: 0;
  }
}

@media (max-width: 599px) {
  .ctc-review__status,
  .ctc-review__flow,
  .ctc-review__hero,
  .ctc-review__section,
  .ctc-review__foot {
    padding-right: 16px;
    padding-left: 16px;
  }
}
</style>
